// pages/ProductComparisonResultPage.tsx
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  postProductCompare,
  getScannerPrefs,
  type ProductCompareResponse,
  type ProductComparePayload,
} from '../api/endpoints';
import { API_BASE_URL } from '../api/config';
import useFavoriteProduct from '../hooks/useFavoriteProduct';
import { motion, useReducedMotion } from 'framer-motion';
import { postScannerCompare } from '../api/telemetry';
import AIDisclaimerFooter from '../components/AIDisclaimerFooter';
import { getErrorMessage } from '../utils/errors';
import {
  comedogenicScore,
  getIrritantLevel,
  ingredientDisplayName,
  labelize,
  shareOrCopyUrl,
} from '../utils/scannerIngredient';
import type { ProductAnalysisSnapshot, ScannerIngredient } from '../types/product';
import { knownBreakdownEntries } from '../utils/scoreBreakdown';

/* ==========================================================
   Icons
   ========================================================== */
const cx = 'w-[18px] h-[18px]';
const IconScale = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v18" /><path d="M5 7h14" />
    <path d="m7 7-4 7a4 4 0 0 0 4 4 4 4 0 0 0 4-4l-4-7Zm10 0-4 7a4 4 0 0 0 4 4 4 4 0 0 0 4-4l-4-7Z" />
  </svg>
);
const IconArrow = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12h18" /><path d="M15 6l6 6-6 6" />
  </svg>
);
const IconBadge = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2 20 7v7c0 5-4 8-8 8s-8-3-8-8V7Z" /><path d="M9 11l2 2 4-4" />
  </svg>
);
const IconShare = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
    <path d="M16 6l-4-4-4 4" /><path d="M12 2v14" />
  </svg>
);
const IconHeart = ({ filled = false }: { filled?: boolean }) => (
  <svg viewBox="0 0 24 24" className={cx} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21s-6.4-4.35-9.2-8.06C1.02 11.66 1 8.65 3.2 6.4 5.4 4.2 8.7 4.1 10.9 6.3L12 7.41l1.1-1.1c2.2-2.2 5.5-2.1 7.7.1 2.2 2.24 2.18 5.26.2 6.54C18.4 16.65 12 21 12 21Z" />
  </svg>
);
const IconBeaker = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M6 2h12M9 2v5l-5 9a4 4 0 0 0 3.5 6h9a4 4 0 0 0 3.5-6L15 7V2" />
    <path d="M8 14h8" />
  </svg>
);
const IconChart = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M4 19h16M8 18V9M12 18V5M16 18v-7" />
  </svg>
);
const IconWarn = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M10.3 2.7 1.6 17.7A2 2 0 0 0 3.3 21h17.4a2 2 0 0 0 1.7-3.3L13.7 2.7a2 2 0 0 0-3.4 0Z" />
    <path d="M12 8v5M12 17h.01" />
  </svg>
);

/* ==========================================================
   Small UI atoms
   ========================================================== */
function Chip({
  children,
  tone = 'neutral',
  block = false,
}: { children: React.ReactNode; tone?: 'success' | 'warn' | 'danger' | 'neutral'; block?: boolean }) {
  const mod =
    tone === 'success' ? 'pill--teal' :
    tone === 'warn'    ? 'pill--coral' :
    tone === 'danger'  ? 'pill--coral' : '';
  return (
    <span className={['pill', mod, block ? 'pill--block' : ''].filter(Boolean).join(' ')}>
      {children}
    </span>
  );
}

function Badge({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'success' | 'warn' | 'danger' | 'neutral' }) {
  const mod =
    tone === 'success' ? 'pill--teal' :
    tone === 'warn'    ? 'pill--coral' :
    tone === 'danger'  ? 'pill--coral' : '';
  return (
    <span className={['pill pill--compact', mod].filter(Boolean).join(' ')}>{children}</span>
  );
}

function SectionCard({
  title, children, icon,
}: {
  title: string; children: React.ReactNode; icon?: React.ReactNode;
}) {
  return (
    <section className="card card--bordered stack-4" style={{ padding: 'var(--s-5)', overflow: 'hidden' }}>
      <div className="row-3">
        {icon && <span style={{ color: 'var(--ink-muted)', flexShrink: 0 }}>{icon}</span>}
        <h3 className="t-small" style={{ margin: 0, fontWeight: 600, color: 'var(--ink)' }}>{title}</h3>
      </div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card card--compact card--bordered card--flat" style={{ minWidth: 0 }}>
      <div className="t-small t-mute">{label}</div>
      <div className="t-small" style={{ fontWeight: 600, color: 'var(--ink)', wordBreak: 'break-all' }}>{value}</div>
    </div>
  );
}

/* ---------------- Score ring ---------------- */
function scoreTone(score: number) {
  if (score >= 75) return { label: 'Strong',   color: 'var(--c-kivo-teal)', stroke: 'var(--c-kivo-teal)' };
  if (score >= 50) return { label: 'Moderate', color: 'var(--c-deep-teal)', stroke: 'var(--c-deep-teal)' };
  if (score >= 30) return { label: 'Caution',  color: 'var(--c-coral)',     stroke: 'var(--c-coral)' };
  return              { label: 'Flagged',  color: 'var(--c-coral)',     stroke: 'var(--c-coral)' };
}
function ScoreRing({ score, size = 140, stroke = 12 }: { score: number; size?: number; stroke?: number }) {
  const pct = Math.max(0, Math.min(100, score));
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const dash = C * (pct / 100);
  const tone = scoreTone(pct);
  return (
    <div className="score-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
        <circle cx={size / 2} cy={size / 2} r={r} style={{ stroke: 'var(--rule)' }} strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          style={{ stroke: tone.stroke }}
          strokeWidth={stroke} strokeLinecap="round" fill="none"
          strokeDasharray={`${dash} ${C - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="score-ring__inner">
        <div className="score-ring__num" style={{ color: tone.color }}>{Math.round(pct)}</div>
        <div className="t-label" style={{ marginTop: 0 }}>Score</div>
        <div className="t-small t-mute">{tone.label}</div>
      </div>
    </div>
  );
}

/* ==========================================================
   Helpers
   ========================================================== */
const normalizeImage = (url?: string | null) => {
  if (!url) return null;
  if (/^https?:/i.test(url)) return url;
  try {
    const absolute = new URL(url.startsWith('/') ? url : `/${url}`, API_BASE_URL);
    return absolute.toString();
  } catch { return null; }
};
const fmt = (n?: number) => (typeof n === 'number' && Number.isFinite(n) ? `${Math.round(n)}` : '—');
const prettyOutcome = (v?: 'a' | 'b' | 'tie') => (v === 'a' ? 'Product A' : v === 'b' ? 'Product B' : 'Tie');

type NavState = { response?: ProductCompareResponse; payload?: ProductComparePayload; productIds?: { a?: number; b?: number } };
type PrefsOut = Awaited<ReturnType<typeof getScannerPrefs>>;
type CompareAnalysis = ProductAnalysisSnapshot;

function isCompareAnalysis(value: unknown): value is CompareAnalysis {
  return typeof value === 'object' && value !== null;
}

function asCompareAnalysis(value: unknown): CompareAnalysis {
  if (isCompareAnalysis(value)) return value;
  return {};
}

function getAnalysisIngredients(product: ProductCompareResponse['product_a'] | undefined): ScannerIngredient[] {
  return asCompareAnalysis(product?.analysis).ingredients ?? [];
}

/* ==========================================================
   Page
   ========================================================== */
export default function ProductComparisonResultPage(): React.JSX.Element {
  const nav = useNavigate();
  const state = (useLocation().state ?? {}) as NavState;
  const [params] = useSearchParams();

  console.log('[PAGE LOAD] ProductComparisonResultPage - Initial state:', {
    has_state_response: !!state.response,
    has_state_payload: !!state.payload,
    state_productIds: state.productIds,
    url_params: { a: params.get('a'), b: params.get('b') }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<ProductCompareResponse | null>(state.response ?? null);
  const [payload, setPayload] = useState<ProductComparePayload | null>(state.payload ?? null);
  const [productIds] = useState(state.productIds);
  const [copied, setCopied] = useState<string | null>(null);
  const [telemetrySent, setTelemetrySent] = useState(false);

  // prefs
  const [prefs, setPrefs] = useState<PrefsOut | null>(null);
  useEffect(() => {
    let alive = true;
    getScannerPrefs()
      .then((p) => { if (alive) setPrefs(p); })
      .catch(() => { if (alive) setPrefs(null); });
    return () => { alive = false; };
  }, []);
  const hasPrefs =
    prefs !== null &&
    (prefs.allergies.length > 0 ||
      prefs.acne_prone ||
      prefs.ethical_mode !== 'off' ||
      prefs.cautious_actives);
  const activePrefs = hasPrefs ? prefs : null;

  // Fetch comparison if not already in state. Supports barcode params (a/b) and
  // product-id params (pa/pb) so a pair can be re-opened from history — including
  // slug-only products without a barcode. Re-running an already-scanned pair is
  // free on the backend (no credits consumed).
  useEffect(() => {
    if (response) return;
    const a = params.get('a'); const b = params.get('b');
    const pa = params.get('pa'); const pb = params.get('pb');
    if ((!a && !pa) || (!b && !pb)) return;
    const p: ProductComparePayload = {
      barcode_a: a ?? '',
      barcode_b: b ?? '',
      ...(pa ? { product_id_a: Number(pa) } : {}),
      ...(pb ? { product_id_b: Number(pb) } : {}),
    };
    setPayload(p); setLoading(true);
    postProductCompare(p)
      .then((res) => {
        setResponse(res);
      })
      .catch((error_: unknown) => { setError(getErrorMessage(error_, 'Failed to load comparison')); })
      .finally(() => { setLoading(false); });
  }, [response, params]);

  // Track telemetry whenever we have a valid response (from API or navigation state)
  useEffect(() => {
    console.log('[TELEMETRY USEEFFECT] Scanner Compare useEffect triggered:', {
      has_response: !!response,
      telemetrySent,
      will_run: !!response && !telemetrySent
    });

    if (!response || telemetrySent) return;

    console.log('[TELEMETRY DEBUG] Scanner Compare Response:', {
      has_product_a: !!response.product_a,
      has_product_b: !!response.product_b,
      product_id_a: response.product_a.product_id,
      product_id_b: response.product_b.product_id,
    });

    if (response.product_a.product_id && response.product_b.product_id) {
      console.log('[TELEMETRY DEBUG] Sending scanner compare telemetry:', response.product_a.product_id, response.product_b.product_id);
      void postScannerCompare(response.product_a.product_id, response.product_b.product_id).catch((error_: unknown) => {
        console.error('[TELEMETRY ERROR] Scanner compare failed:', error_);
      });
      setTelemetrySent(true);
    } else {
      console.warn('[TELEMETRY DEBUG] Missing product IDs, cannot send scanner compare telemetry');
    }
  }, [response, telemetrySent]);

  const productA = response?.product_a;
  const productB = response?.product_b;
  const comparison = response?.comparison;

  const favoriteA = useFavoriteProduct({ productId: productA?.product_id, disabled: !productA?.product_id });
  const favoriteB = useFavoriteProduct({ productId: productB?.product_id, disabled: !productB?.product_id });

  const betterOverall = comparison?.better_overall;
  const betterForUser = comparison?.better_for_user;

  const share = async () => {
    const url = window.location.href;
    try {
      await shareOrCopyUrl('Comparison result', url, () => {
        setCopied('Link copied');
        setTimeout(() => { setCopied(null); }, 1500);
      });
    } catch {
      /* share/copy cancelled or unavailable */
    }
  };

  const retry = async () => {
    if (!payload) { nav('/comparison'); return; }
    setLoading(true); setError(null);
    try { setResponse(await postProductCompare(payload)); }
    catch (error_: unknown) { setError(getErrorMessage(error_, 'Failed to refresh comparison')); }
    finally { setLoading(false); }
  };

  const prefersReduced = useReducedMotion() ?? false;
  const scoreBreakdownA = productA ? asCompareAnalysis(productA.analysis).score_breakdown : undefined;
  const scoreBreakdownB = productB ? asCompareAnalysis(productB.analysis).score_breakdown : undefined;

  return (
    <>
      <div className="stack-5" style={{ minWidth: 0 }}>

        {/* Free re-scan: this pair was already in history, so no credits were used. */}
        {response?.charged === false && (
          <div className="pill pill--teal" style={{ alignSelf: 'flex-start' }}>
            <span className="dot" />
            Already scanned — no tokens used
          </div>
        )}

        {/* ── Hero ─────────────────────────────────────────── */}
        <div className="card card--bordered" style={{ position: 'relative', overflow: 'hidden', padding: 0 }}>
          <div
            className="scan-pattern"
            style={{ position: 'absolute', inset: 0, opacity: 0.3, pointerEvents: 'none' }}
          />
          <div className="stack-4" style={{ position: 'relative', padding: 'var(--s-5)' }}>

            {/* Top row: icon + actions */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s-3)' }}>
              <div
                className="icon-ring"
                style={{ width: 'var(--s-7)', height: 'var(--s-7)', flexShrink: 0 }}
              >
                <IconScale />
              </div>
              <div className="row-3" style={{ flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button onClick={share} className="btn btn--ghost btn--sm">
                  <IconShare />Share
                </button>
                <button
                  onClick={() => { nav('/comparison', {
                    state: {
                      prefill: {
                        barcodeA: payload?.barcode_a, barcodeB: payload?.barcode_b,
                        productIdA: productA?.product_id ?? productIds?.a,
                        productIdB: productB?.product_id ?? productIds?.b,
                      }
                    }
                  }); }}
                  className="btn btn--ghost btn--sm"
                >
                  New comparison
                </button>
              </div>
            </div>

            {/* Title + description */}
            <div style={{ minWidth: 0 }}>
              <h1 className="t-h2" style={{ margin: 0 }}>
                Comparison <em className="t-italic t-teal">result.</em>
              </h1>
              <p className="t-small t-mute" style={{ margin: 'var(--s-2) 0 0' }}>
                Side-by-side ingredient intelligence. The stronger match is highlighted.
              </p>
            </div>

            {/* Outcome chips */}
            {comparison && (
              <div className="stack-2">
                <Chip tone={betterOverall === 'tie' ? 'neutral' : 'success'} block>
                  Better overall: {prettyOutcome(betterOverall)}
                </Chip>
                <Chip tone={betterForUser === 'tie' ? 'neutral' : 'success'} block>
                  Better for you: {prettyOutcome(betterForUser)}
                </Chip>
              </div>
            )}

            {/* Preference flags */}
            {activePrefs && (
              <div className="g-2">
                {activePrefs.acne_prone && <Chip tone="warn" block>Breakout-prone</Chip>}
                {activePrefs.cautious_actives && <Chip tone="warn" block>Gentle actives</Chip>}
                {activePrefs.ethical_mode !== 'off' &&
                  <Chip block>Ethical: {labelize(activePrefs.ethical_mode)}</Chip>}
                {activePrefs.allergies.length > 0 &&
                  <Chip block>
                    {activePrefs.allergies.length} allergen{activePrefs.allergies.length > 1 ? 's' : ''} selected
                  </Chip>}
              </div>
            )}
          </div>
        </div>

        {/* ── Loading ────────────────────────────────────────── */}
        {loading && (
          <div className="card card--bordered" style={{ padding: 'var(--s-5)' }}>
            <p className="t-small t-mute" style={{ margin: 0 }}>Loading comparison…</p>
          </div>
        )}

        {/* ── Error ─────────────────────────────────────────── */}
        {error && (
          <div className="card card--alert" style={{ padding: 'var(--s-5)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s-3)' }}>
              <p className="t-small" style={{ margin: 0, color: 'var(--c-coral)', minWidth: 0 }}>{error}</p>
              <button
                onClick={retry}
                className="btn btn--ghost btn--sm"
                style={{ flexShrink: 0, color: 'var(--c-coral)', borderColor: 'var(--c-coral)' }}
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* ── Product panels ─────────────────────────────────── */}
        {productA && productB && (
          <div className="g-2" style={{ minWidth: 0 }}>
            <ProductPanel
              side="a"
              product={productA}
              barcode={productA.barcode}
              favorite={favoriteA}
              highlight={betterOverall === 'a' || betterForUser === 'a'}
            />
            <ProductPanel
              side="b"
              product={productB}
              barcode={productB.barcode}
              favorite={favoriteB}
              highlight={betterOverall === 'b' || betterForUser === 'b'}
            />
          </div>
        )}

        {/* ── Outcomes ──────────────────────────────────────── */}
        {comparison && (
          <SectionCard title="Outcomes" icon={<IconArrow />}>
            <div className="g-2">
              <OutcomeCard title="Better overall" value={prettyOutcome(betterOverall)} delta={comparison.delta_overall} />
              <OutcomeCard title="Better for you" value={prettyOutcome(betterForUser)} delta={comparison.delta_for_user} />
            </div>
          </SectionCard>
        )}

        {/* ── Score breakdown ────────────────────────────────── */}
        {(scoreBreakdownA ?? scoreBreakdownB) && (
          <SectionCard title="Score breakdown" icon={<IconChart />}>
            <div className="g-2">
              <BreakdownCard side="a" breakdown={scoreBreakdownA} prefersReduced={prefersReduced} />
              <BreakdownCard side="b" breakdown={scoreBreakdownB} prefersReduced={prefersReduced} />
            </div>
          </SectionCard>
        )}

        {/* ── Head-to-head ───────────────────────────────────── */}
        {productA && productB && (
          <SectionCard title="Head-to-head highlights" icon={<IconScale />}>
            <HeadToHead a={productA} b={productB} />
          </SectionCard>
        )}

        {/* ── Ingredients ────────────────────────────────────── */}
        {productA && productB && (
          <SectionCard title="Ingredients (full lists)" icon={<IconBeaker />}>
            <div className="g-2">
              <IngredientsAccordion side="a" ingredients={getAnalysisIngredients(productA)} />
              <IngredientsAccordion side="b" ingredients={getAnalysisIngredients(productB)} />
            </div>
          </SectionCard>
        )}
      </div>

      {/* ── Toast ──────────────────────────────────────────── */}
      {copied && (
        <div style={{ position: 'fixed', bottom: 'var(--s-6)', left: '50%', transform: 'translateX(-50%)', zIndex: 50 }}>
          <span className="pill" style={{ background: 'var(--c-charcoal)', color: 'var(--c-cream)' }}>
            {copied}
          </span>
        </div>
      )}

      <AIDisclaimerFooter />
    </>
  );
}

/* ==========================================================
   Subcomponents
   ========================================================== */
function OutcomeCard({ title, value, delta }: { title: string; value: string; delta: number }) {
  return (
    <div className="card card--sand card--bordered card--compact card--flat" style={{ minWidth: 0 }}>
      <div className="row-3 t-label" style={{ color: 'var(--ink-muted)', marginBottom: 'var(--s-2)' }}>
        <IconArrow />{title}
      </div>
      <div className="t-h3" style={{ margin: 0 }}>{value}</div>
      <div className="t-small t-mute" style={{ marginTop: 'var(--s-1)' }}>
        Delta: {typeof delta === 'number' && Number.isFinite(delta) ? delta.toFixed(1) : '—'}
      </div>
    </div>
  );
}

function ProductPanel({
  side, product, barcode, favorite, highlight,
}: {
  side: 'a' | 'b';
  product: ProductCompareResponse['product_a'];
  barcode: string;
  favorite: { favorited: boolean | null; loading: boolean; toggle: () => void };
  highlight: boolean;
}) {
  const label = `Product ${side.toUpperCase()}`;
  const analysis = asCompareAnalysis(product.analysis);
  const imageSrc = normalizeImage(product.image_url);

  const fav = favorite.favorited;
  const ariaPressed = fav ?? undefined;
  const scoreVal = analysis.score ?? analysis.score_raw ?? 0;
  const ingredients = analysis.ingredients ?? [];

  return (
    <div
      className={['card card--bordered stack-4 compare-result-side', `compare-result-side--${side}`, highlight ? 'card--highlight' : ''].filter(Boolean).join(' ')}
      data-testid={`compare-result-product-${side}`}
      style={{ padding: 'var(--s-4)', minWidth: 0 }}
    >
      {/* Label row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s-2)' }}>
        <span className="t-label compare-result-side__label">
          <span className="compare-result-side__marker" data-testid={`compare-side-marker-${side}`} aria-hidden />{label}
        </span>
        {highlight && (
          <span className="pill pill--teal" style={{ gap: 'var(--s-1)' }}>
            <IconBadge /> Highlighted
          </span>
        )}
      </div>

      {/* Image + score ring */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s-4)', alignItems: 'center' }}>
        <div style={{ display: 'grid', placeItems: 'center' }}>
          {imageSrc ? (
            <div
              className="frame"
              style={{ width: 140, height: 140, display: 'grid', placeItems: 'center' }}
            >
              <img
                src={imageSrc}
                alt={label}
                style={{ maxHeight: 120, maxWidth: 110, objectFit: 'contain' }}
              />
            </div>
          ) : (
            <div
              style={{
                width: 140, height: 140,
                borderRadius: 'var(--r-md)',
                border: '1.5px dashed var(--c-charcoal-30)',
                display: 'grid', placeItems: 'center',
              }}
            >
              <span className="t-small t-mute">No image</span>
            </div>
          )}
        </div>
        <div style={{ display: 'grid', placeItems: 'center' }}>
          <ScoreRing score={scoreVal} size={140} />
        </div>
      </div>

      {/* Product details */}
      <div style={{ minWidth: 0 }}>
        <div className="t-small" style={{ fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {analysis.name ?? 'Unknown product'}
        </div>
        <div className="t-small t-mute" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {analysis.brand ?? 'Brand unavailable'}
        </div>

        {/* Meta chips */}
        <div className="row-3" style={{ flexWrap: 'wrap', marginTop: 'var(--s-2)' }}>
          <StatChip label="Barcode" value={barcode} />
          {typeof product.product_id === 'number' && (
            <span className="pill">ID {product.product_id}</span>
          )}
        </div>

        {/* Stats grid */}
        <div className="g-2" style={{ marginTop: 'var(--s-3)' }}>
          <Stat label="Score" value={fmt(scoreVal)} />
          <Stat label="Ingredients" value={fmt(ingredients.length)} />
        </div>

        {/* Actions */}
        <div className="stack-2" style={{ marginTop: 'var(--s-3)' }}>
          <button
            type="button"
            onClick={favorite.toggle}
            disabled={favorite.loading || !product.product_id}
            aria-pressed={ariaPressed}
            aria-label={fav ? 'Remove from favorites' : 'Add to favorites'}
            className="btn btn--ghost btn--sm btn--full"
            style={fav ? { color: 'var(--c-coral)', borderColor: 'var(--c-coral)' } : undefined}
          >
            <IconHeart filled={Boolean(fav)} />
            {fav ? 'Remove from favorites' : 'Add to favorites'}
          </button>

          {typeof product.product_id === 'number' && (
            <button
              type="button"
              onClick={() => window.open(`/favorites/${product.product_id}`, '_self')}
              className="btn btn--ghost btn--sm btn--full"
            >
              View details
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatChip({ label, value }: { label: string; value: string }) {
  return (
    <span className="pill">
      <span className="t-mute" style={{ whiteSpace: 'nowrap' }}>{label}</span>
      {' '}
      <span style={{ fontFamily: 'ui-monospace, monospace' }}>{value}</span>
    </span>
  );
}

/* ── Score breakdown ─────────────────────────────────────── */
function BreakdownCard({
  side, breakdown, prefersReduced,
}: {
  side: 'a' | 'b';
  breakdown?: Record<string, number>;
  prefersReduced: boolean;
}) {
  const label = `Product ${side.toUpperCase()}`;
  if (!breakdown) return (
    <div className={`card card--sand card--bordered card--flat compare-result-side compare-result-side--${side}`} style={{ padding: 'var(--s-4)', minWidth: 0 }}>
      <div className="t-label compare-result-side__label" style={{ marginBottom: 'var(--s-2)' }}><span className="compare-result-side__marker" aria-hidden />{label}</div>
      <p className="t-small t-mute" style={{ margin: 0 }}>No breakdown available.</p>
    </div>
  );

  const nonZero = Object.fromEntries(Object.entries(breakdown).filter(([, v]) => Math.abs(v) > 0));
  const hasAny = Object.keys(nonZero).length > 0;

  return (
    <div className={`card card--sand card--bordered card--flat compare-result-side compare-result-side--${side}`} style={{ padding: 'var(--s-4)', minWidth: 0 }}>
      <div className="t-label compare-result-side__label" style={{ marginBottom: 'var(--s-3)' }}><span className="compare-result-side__marker" aria-hidden />{label}</div>
      {hasAny
        ? <Breakdown breakdown={nonZero} prefersReduced={prefersReduced} />
        : <p className="t-small t-mute" style={{ margin: 0 }}>All factors neutral.</p>}
    </div>
  );
}

function Breakdown({ breakdown, prefersReduced }: { breakdown?: Record<string, number>; prefersReduced: boolean }) {
  if (!breakdown) return null;

  const iconFor = (k: string) => {
    const n = k.toLowerCase();
    if (n.includes('irritat') || n.includes('photo')) return <IconWarn />;
    if (n.includes('active') || n.includes('bonus') || n.includes('preserv') || n.includes('fragrance')) return <IconBeaker />;
    return <IconChart />;
  };

  const entries = knownBreakdownEntries(breakdown).map(([k, v]) => {
    const isBonus = k.includes('bonus');
    const label = labelize(k.replace(/_penalty|_bonus/g, ''));
    const value = typeof v === 'number' ? v : 0;
    const tone = isBonus ? 'success' : value >= 12 ? 'danger' : value > 0 ? 'warn' : 'neutral';
    return { key: k, label, value, isBonus, tone };
  });

  const maxVal = Math.max(1, ...entries.map((e) => Math.abs(e.value)));

  return (
    <div className="stack-3" style={{ minWidth: 0 }}>
      {entries.map((e) => {
        const width = Math.min(100, Math.round((Math.abs(e.value) / maxVal) * 100));
        const barMod = e.isBonus ? '' : e.tone === 'danger' ? 'result-bar--coral' : 'result-bar--muted';
        const numColor = e.isBonus ? 'var(--c-kivo-teal)' : e.tone === 'danger' ? 'var(--c-coral)' : 'var(--ink-muted)';
        return (
          <div key={e.key} style={{ minWidth: 0 }}>
            <div className="row-3" style={{ justifyContent: 'space-between', marginBottom: 'var(--s-1)' }}>
              <div className="row-3" style={{ minWidth: 0, overflow: 'hidden' }}>
                <span style={{ color: 'var(--ink-muted)', flexShrink: 0 }}>{iconFor(e.key)}</span>
                <span className="t-small" style={{ fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.label}
                </span>
              </div>
              <span className="t-small" style={{ fontWeight: 600, color: numColor, flexShrink: 0 }}>
                {e.isBonus ? '+' : '−'}{Math.abs(e.value)}
              </span>
            </div>
            <div className={`result-bar ${barMod}`} aria-hidden>
              <motion.div
                className="result-bar__fill"
                initial={{ width: prefersReduced ? `${width}%` : 0 }}
                animate={{ width: `${width}%` }}
                transition={prefersReduced ? { duration: 0 } : { type: 'spring', stiffness: 260, damping: 28 }}
              />
              <div className="result-bar__gloss" />
            </div>
          </div>
        );
      })}
      <p className="t-small t-mute" style={{ margin: 0 }}>Penalties reduce score; bonuses increase it.</p>
    </div>
  );
}

const toStringList = (v: string[] | undefined): string[] => (Array.isArray(v) ? v : []);
const uniqueItems = (x: string[], y: string[]) => x.filter((i) => !y.includes(i));

/* ── Head-to-head ────────────────────────────────────────── */
function HeadToHead({ a, b }: { a: ProductCompareResponse['product_a']; b: ProductCompareResponse['product_b'] }) {
  const A = asCompareAnalysis(a.analysis);
  const B = asCompareAnalysis(b.analysis);

  const aActives = toStringList(A.actives); const bActives = toStringList(B.actives);
  const aIrr = toStringList(A.potential_irritants); const bIrr = toStringList(B.potential_irritants);
  const aPhoto = toStringList(A.photosensitizers); const bPhoto = toStringList(B.photosensitizers);

  return (
    <div className="g-2">
      <SideHighlights side="a" label="A — Standouts" items={uniqueItems(aActives, bActives)} tone="success" empty="No unique strengths detected." />
      <SideHighlights side="b" label="B — Standouts" items={uniqueItems(bActives, aActives)} tone="success" empty="No unique strengths detected." />
      <SideHighlights side="a" label="A — Watchouts" items={[...aIrr, ...aPhoto]} tone="warn" empty="No specific cautions." />
      <SideHighlights side="b" label="B — Watchouts" items={[...bIrr, ...bPhoto]} tone="warn" empty="No specific cautions." />
    </div>
  );
}

function SideHighlights({ side, label, items, tone, empty }: { side: 'a' | 'b'; label: string; items: string[]; tone: 'success' | 'warn' | 'danger'; empty: string }) {
  return (
    <div className={`card card--bordered card--flat compare-result-side compare-result-side--${side}`} style={{ padding: 'var(--s-4)', minWidth: 0 }}>
      <div className="t-label compare-result-side__label" style={{ marginBottom: 'var(--s-2)' }}><span className="compare-result-side__marker" aria-hidden />{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)' }}>
        {items.length > 0
          ? items.slice(0, 20).map((x, i) => <Chip key={`${label}-${i}`} tone={tone}>{x}</Chip>)
          : <span className="t-small t-mute">{empty}</span>}
      </div>
    </div>
  );
}

/* ── Ingredients accordion ───────────────────────────────── */
function IngredientsAccordion({ side, ingredients }: { side: 'a' | 'b'; ingredients: ScannerIngredient[] }) {
  const [open, setOpen] = useState(false);
  const count = ingredients.length;
  const label = `Product ${side.toUpperCase()}`;

  return (
    <div className={`card card--bordered card--flat compare-result-side compare-result-side--${side}`} style={{ padding: 0, overflow: 'hidden', minWidth: 0 }}>
      <button
        type="button"
        onClick={() => { setOpen((s) => !s); }}
        style={{
          display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--s-4) var(--s-5)', background: 'transparent', border: 'none', cursor: 'pointer',
        }}
        aria-expanded={open}
      >
        <div className="row-3" style={{ minWidth: 0 }}>
          <span style={{ color: 'var(--ink-muted)' }}><IconBeaker /></span>
          <span className="compare-result-side__marker" aria-hidden />
          <span className="t-small" style={{ fontWeight: 600, color: 'var(--ink)' }}>{label}</span>
          <span className="t-small t-mute">{count} total</span>
        </div>
        <Chevron open={open} />
      </button>

      <motion.div
        initial={false}
        animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }}
        style={{ overflow: 'hidden', borderTop: open ? '1px solid var(--rule)' : 'none' }}
      >
        <div style={{ padding: 'var(--s-4) var(--s-5)' }}>
          {count ? (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--s-2)' }}>
              {ingredients.map((ing, i) => (
                <IngredientRow key={`${ing.id ?? i}-${ing.inci_name ?? i}`} ing={ing} />
              ))}
            </ul>
          ) : (
            <p className="t-small t-mute" style={{ margin: 0 }}>No ingredients available.</p>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function IngredientRow({ ing }: { ing: ScannerIngredient }) {
  const flags: { label: string; tone: 'success' | 'warn' | 'danger' | 'neutral' }[] = [];

  const irr = getIrritantLevel(ing);
  if (irr === 'likely')   flags.push({ label: 'Irritant (likely)',   tone: 'danger' });
  if (irr === 'possible') flags.push({ label: 'Irritant (possible)', tone: 'warn' });

  if (ing.photosensitizer)           flags.push({ label: 'Photosensitizer',    tone: 'warn' });
  if (ing.fragrance)                 flags.push({ label: 'Fragrance',          tone: 'neutral' });
  if (ing.preservative)              flags.push({ label: 'Preservative',       tone: 'neutral' });
  const comedogenic = comedogenicScore(ing);
  if (comedogenic !== null && comedogenic > 0)
    flags.push({ label: `Comedogenic ${comedogenic}/5`, tone: 'danger' });
  if (ing.active) flags.push({ label: 'Active', tone: 'success' });

  const dotClass =
    ing.active              ? 'concern-dot concern-dot--teal' :
    irr === 'likely'         ? 'concern-dot concern-dot--coral' :
    irr === 'possible'       ? 'concern-dot concern-dot--coral' :
    'concern-dot';

  return (
    <li>
      <Link
        to={`/ingredients/${ing.id ?? ''}`}
        target="_blank"
        rel="noopener noreferrer"
        className="product-result"
        style={{ padding: 'var(--s-3)' }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--s-2)', minWidth: 0 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {ingredientDisplayName(ing)}
            </div>
            {(ing.functions?.length ?? 0) > 0 ? (
              <div className="t-small t-mute" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(ing.functions ?? []).join(', ')}
              </div>
            ) : null}
          </div>
          <div className={dotClass} style={{ marginTop: 'var(--s-1)', flexShrink: 0 }} aria-hidden />
        </div>

        {flags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-1)', marginTop: 'var(--s-2)' }}>
            {flags.map((f, idx) => (
              <Badge key={idx} tone={f.tone}>{f.label}</Badge>
            ))}
          </div>
        )}
      </Link>
    </li>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24" width="18" height="18"
      aria-hidden="true"
      style={{
        color: 'var(--ink-muted)',
        flexShrink: 0,
        transition: `transform var(--t-fast) var(--ease)`,
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
    >
      <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
